import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { RiCloseLine, RiArrowLeftSLine, RiArrowRightSLine } from '@remixicon/react';

/**
 * Full-screen image gallery overlay. Distinct from <ImageCarousel> which is
 * an inline in-card carousel — this one is a modal lightbox with a
 * thumbnail strip, keyboard nav, and body-scroll lock.
 */
const ImageGalleryOverlay = ({
  isOpen,
  onClose,
  images = [],
  initialIndex = 0,
  alt = 'Image',
}) => {
  const [index, setIndex] = useState(initialIndex);

  const count = images.length;
  const hasMany = count > 1;

  // Reset index whenever the overlay is (re-)opened.
  useEffect(() => {
    if (isOpen) {
      const safe = Math.min(Math.max(initialIndex, 0), Math.max(count - 1, 0));
      setIndex(safe);
    }
  }, [isOpen, initialIndex, count]);

  const goPrev = useCallback(() => {
    if (!hasMany) return;
    setIndex((prev) => (prev === 0 ? count - 1 : prev - 1));
  }, [hasMany, count]);

  const goNext = useCallback(() => {
    if (!hasMany) return;
    setIndex((prev) => (prev === count - 1 ? 0 : prev + 1));
  }, [hasMany, count]);

  // Lock body scroll + wire keyboard shortcuts while open.
  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
    };
    window.addEventListener('keydown', handleKey);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKey);
    };
  }, [isOpen, onClose, goPrev, goNext]);

  if (!isOpen || count === 0) return null;

  const currentSrc = images[index];
  const stop = (e) => e.stopPropagation();

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex flex-col bg-black/90 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Image gallery"
    >
      {/* Close */}
      <button
        type="button"
        onClick={(e) => { stop(e); onClose?.(); }}
        className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
        aria-label="Close gallery"
      >
        <RiCloseLine className="w-6 h-6" />
      </button>

      {/* Main image — capped below full-height so the bottom thumbnail
          strip and close button always have breathing room. */}
      <div className="flex-1 min-h-0 flex items-center justify-center px-4 sm:px-16 pt-16 pb-6" onClick={stop}>
        <img
          src={currentSrc}
          alt={`${alt} ${index + 1} of ${count}`}
          className="max-w-full max-h-full object-contain rounded-lg shadow-2xl select-none"
          draggable={false}
        />
      </div>

      {/* Prev / Next */}
      {hasMany && (
        <>
          <button
            type="button"
            onClick={(e) => { stop(e); goPrev(); }}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            aria-label="Previous image"
          >
            <RiArrowLeftSLine className="w-7 h-7" />
          </button>
          <button
            type="button"
            onClick={(e) => { stop(e); goNext(); }}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
            aria-label="Next image"
          >
            <RiArrowRightSLine className="w-7 h-7" />
          </button>
        </>
      )}

      {/* Thumbnail strip — flex-shrink-0 so it stays fully visible even
          when the viewport is short (the image div gives up space, not us). */}
      {hasMany && (
        <div className="flex-shrink-0 flex gap-2 overflow-x-auto px-4 py-4 bg-black/40" onClick={stop}>
          {images.map((src, i) => (
            <button
              key={`${src}-${i}`}
              type="button"
              onClick={() => setIndex(i)}
              className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden transition-all ${
                i === index ? 'ring-2 ring-white opacity-100' : 'opacity-60 hover:opacity-100'
              }`}
              aria-label={`Show image ${i + 1}`}
              aria-current={i === index ? 'true' : undefined}
            >
              <img
                src={src}
                alt={`${alt} thumbnail ${i + 1}`}
                className="w-full h-full object-cover"
                draggable={false}
              />
            </button>
          ))}
        </div>
      )}
    </div>,
    document.body
  );
};

export default ImageGalleryOverlay;
